import { Language, Subsidiary } from '@coolblue-development/constants';
import {
  Box,
  Flex,
  freddyTheme,
  Grid,
  GridItem,
  List,
  ListItem,
  Typography
} from '@coolblue-development/freddy';
import { Insurance as InsuranceIcon } from '@coolblue-development/icons';
import { formatCurrency } from '@coolblue-development/next-formatting-utils/core';
import { InsuranceExplanationIcon } from '@coolblue-development/next-insurance/client';
import { SquareProductImage } from '@coolblue-development/next-patterns/client';
import styled from '@emotion/styled';
import hasMonthlyInsurances from '@features/product/components/Insurance/utils/hasMonthlyInsurances';
import type { Insurance } from '@features/product/typings';
import { InsurancePaymentType } from '@features/product/typings';
import { InsuranceProvider } from '@features/product/typings';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import { ComponentProps } from 'react';

type Props = {
  insurance: Insurance;
  productType: string;
  primaryProductId: number;
  primaryProductImageId: string;
  primaryProductName: string;
};

export default function PackagesBlock({
  insurance,
  productType,
  primaryProductId,
  primaryProductImageId,
  primaryProductName
}: Props) {
  const { t, Trans } = useTranslation('webshop.productPage.insurance');
  const testId = useTestId('insurance-package');
  const provider = insurance.partner;
  const liabilityDescription =
    provider === InsuranceProvider.ERGO
      ? insurance.summary?.liabilityCondition?.description
      : hasMonthlyInsurances(insurance)
        ? t('liabilityDescription.monthly')
        : t('liabilityDescription.oneTime');

  const getExplanationIconProps = (title: string) => ({
    productId: primaryProductId,
    title,
    trackingSubFeature: 'product_page' as ComponentProps<
      typeof InsuranceExplanationIcon
    >['trackingSubFeature']
  });

  return (
    <Container>
      <Grid
        templateColumns='1fr auto'
        templateAreas={{
          xs: '"title title" "packages packages"',
          md: '"title image" "packages image"',
          lg: '"title image" "packages packages"',
          xl: '"title image" "packages image"'
        }}
        columnGap={600}
        rowGap={400}
      >
        <GridItem area='title'>
          <Typography variant='h3'>
            {insurance.summary.instructions.title ?? t('packagesBlockTitle', { productType })}
          </Typography>
          <Typography>
            {insurance.summary.instructions.description ??
              t('packagesBlockDescription', { productType })}
          </Typography>
        </GridItem>
        <GridItem area='packages'>
          {insurance.packages.map((insurancePackage, index) => (
            <Box mb={400} key={`${insurancePackage.title}-${index}`} {...testId}>
              {insurancePackage.title && (
                <Typography variant='h5'>{insurancePackage.title} </Typography>
              )}
              <List sx={{ width: '100%' }}>
                {insurancePackage.products.map((insuranceProduct, index) => {
                  const isOneTimePayment =
                    insuranceProduct.paymentType === InsurancePaymentType.ONE_TIME;
                  const paymentTypeTranslationKey = isOneTimePayment
                    ? 'annualPayment'
                    : 'monthlyPayment';
                  const yearlyPriceTranslationKey = isOneTimePayment
                    ? insuranceProduct.yearlyPremium?.includingVat &&
                      insuranceProduct.durationInYears > 1
                      ? 'annualPaymentWithYearly'
                      : 'annualPaymentWithoutYearly'
                    : insuranceProduct.yearlyPremium?.includingVat
                      ? 'monthlyPaymentWithYearly'
                      : 'monthlyPaymentWithoutYearly';

                  return (
                    <ListItem key={`${insuranceProduct.title}-${index}`}>
                      <Flex
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent={{ xs: 'space-between', md: 'flex-start' }}
                        sx={{ width: '100%' }}
                        inline
                      >
                        <Box display='inline'>
                          <Box display={{ xs: 'block', md: 'inline' }} mr={100}>
                            {t(paymentTypeTranslationKey, {
                              duration: insuranceProduct.durationInYears
                            })}
                          </Box>
                          <Trans
                            components={{
                              strong: <strong></strong>
                            }}
                            i18nKey={yearlyPriceTranslationKey}
                            t={t}
                            values={{
                              price: formatCurrency({
                                amount: insuranceProduct.premium?.includingVat,
                                language: Language.DUTCH,
                                subsidiary: Subsidiary.NL,
                                options: {
                                  hideCurrencySymbol: true
                                }
                              }),
                              yearlyPrice: formatCurrency({
                                amount: insuranceProduct.yearlyPremium?.includingVat,
                                language: Language.DUTCH,
                                subsidiary: Subsidiary.NL,
                                options: {
                                  hideCurrencySymbol: true
                                }
                              })
                            }}
                          />
                        </Box>
                        <Flex direction='column' justifyContent='center'>
                          {provider === InsuranceProvider.CoverGenius && (
                            <InsuranceExplanationIcon
                              {...getExplanationIconProps(insuranceProduct.title)}
                              insuranceQuoteId={insuranceProduct.id}
                              provider={InsuranceProvider.CoverGenius}
                            />
                          )}
                          {provider === InsuranceProvider.ERGO && (
                            <InsuranceExplanationIcon
                              {...getExplanationIconProps(insuranceProduct.title)}
                              insuranceProductId={insuranceProduct.id}
                              provider={InsuranceProvider.ERGO}
                            />
                          )}
                        </Flex>
                      </Flex>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}

          {liabilityDescription && <Typography mt={400}>{liabilityDescription}</Typography>}
        </GridItem>
        <GridItem area='image' hideUntil='md'>
          <SquareProductImage
            alt={primaryProductName}
            background='transparent'
            imageId={primaryProductImageId}
            size='large'
            icon={<InsuranceIcon color={freddyTheme.color.icon.base} innerFill='contrast' />}
            iconPosition='left'
            fileCollection='product'
            loading='lazy'
          />
        </GridItem>
      </Grid>
    </Container>
  );
}

const Container = styled.div`
  height: 100%;
  padding: ${({ theme }) => theme.spacing[600]};
  border-radius: ${({ theme }) => theme.borderRadius[100]};
  background-color: ${({ theme }) => theme.color.background.secondary};
`;
